import { useEffect, useRef, useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { PipecatClient } from '@pipecat-ai/client-js'
import { SmallWebRTCTransport } from '@pipecat-ai/small-webrtc-transport'
import { Room, RoomEvent } from 'livekit-client'
import { CheckCircle2, Clock, Loader2, Mic, MicOff, Phone, PhoneOff, Sparkles, Video, VideoOff, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import DevicePickers from '@/components/DevicePickers'
import AudioOrb from '@/components/AudioOrb'
import { createLatencyProbe } from '@/lib/latencyProbe'
import doctorImage from '../../../assets/images/doctor.webp'

function pickRecorderMimeType() {
  if (typeof MediaRecorder === 'undefined') return null
  const candidates = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4']
  for (const t of candidates) {
    if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t)) return t
  }
  return null
}

const LANGUAGES = [
  'Any',
  'English',
  'Spanish',
  'French',
  'German',
  'Italian',
  'Portuguese',
  'Mandarin Chinese',
  'Japanese',
  'Hindi',
  'Arabic',
  'Hebrew',
]

function decodeB64Utf8(b64) {
  const binary = atob(b64)
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0))
  return new TextDecoder('utf-8').decode(bytes)
}

function encodeB64Utf8(str) {
  const bytes = new TextEncoder().encode(str)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
  return btoa(binary)
}

function systemPromptForLanguage(b64, language) {
  if (!b64 || !language || language === 'English' || language === 'Any') return b64
  try {
    const decoded = decodeB64Utf8(b64)
    const directive = `\n\n# Language\nYou and the clinician are speaking in ${language}. ALL of your spoken responses MUST be in ${language}, including your very first message. Do not switch to English even if examples in these instructions are written in English.`
    return encodeB64Utf8(decoded + directive)
  } catch (e) {
    console.warn('Could not apply language to system prompt:', e)
    return b64
  }
}

function formatClock(seconds) {
  if (seconds < 0) seconds = 0
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

const VERDICT_META = {
  correct: { label: 'Correct diagnosis', icon: CheckCircle2, tone: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  partial: { label: 'Partially correct', icon: Sparkles, tone: 'text-amber-700 bg-amber-50 border-amber-200' },
  incorrect: { label: 'Incorrect diagnosis', icon: XCircle, tone: 'text-rose-700 bg-rose-50 border-rose-200' },
  missing: { label: 'No diagnosis submitted', icon: XCircle, tone: 'text-slate-700 bg-slate-50 border-slate-200' },
}

export default function TrainingSession({
  conversationUuid,
  agentSlug,
  caseName,
  caseDescription,
  timeLimitSeconds = 120,
  systemPromptB64,
  avatarId,
  avatarPreviewUrl,
  voiceGender,
  voiceMap,
  defaultModel = 'gemini-live',
  grade: initialGrade = null,
  traineeDiagnosis: initialDiagnosis = '',
}) {
  const voiceMapB64 = voiceMap && Object.keys(voiceMap).length > 0 ? btoa(JSON.stringify(voiceMap)) : null
  const avatarVideoRef = useRef(null)
  const localVideoRef = useRef(null)
  const remoteAudioRef = useRef(null)
  const clientRef = useRef(null)
  const avatarRoomRef = useRef(null)
  const avatarSessionRef = useRef(null)
  const mediaStreamRef = useRef(null)
  const recorderRef = useRef(null)
  const recorderChunksRef = useRef([])
  const recorderContextRef = useRef(null)
  const callStartedRef = useRef(false)
  const timerRef = useRef(null)
  const autoEndedRef = useRef(false)
  const latencyRef = useRef(null)

  const [status, setStatus] = useState('idle')
  const [muted, setMuted] = useState(false)
  const [camOff, setCamOff] = useState(false)
  const [error, setError] = useState('')
  const [hasAvatarVideo, setHasAvatarVideo] = useState(false)
  const [hasVideo, setHasVideo] = useState(false)
  const [avatarNotice, setAvatarNotice] = useState('')
  const [secondsLeft, setSecondsLeft] = useState(timeLimitSeconds)
  const [showDiagnosisForm, setShowDiagnosisForm] = useState(Boolean(initialGrade))
  const [diagnosis, setDiagnosis] = useState(initialDiagnosis || '')
  const [grading, setGrading] = useState(false)
  const [grade, setGrade] = useState(initialGrade)
  const [language, setLanguage] = useState('Any')
  const [pcClient, setPcClient] = useState(null)
  const model = defaultModel

  const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content

  const attachLocalVideoTrack = (client = clientRef.current) => {
    const videoTrack = client?.tracks()?.local?.video
    const video = localVideoRef.current
    if (!videoTrack || !video) { setHasVideo(false); return }

    // Short-circuit if the same track is already attached. The countdown
    // re-renders this component every second, which re-fires the video's
    // inline ref callback; without this guard we'd rebuild MediaStream and
    // reassign srcObject on every tick, causing visible flicker.
    const existing = mediaStreamRef.current
    const existingTracks = existing ? existing.getVideoTracks() : []
    const alreadyAttached =
      existingTracks.length === 1 &&
      existingTracks[0] === videoTrack &&
      video.srcObject === existing
    if (alreadyAttached) {
      setHasVideo(true)
      maybeStartRecording()
      return
    }

    const stream = new MediaStream([videoTrack])
    mediaStreamRef.current = stream
    video.srcObject = stream
    setHasVideo(true)
    video.play().catch(() => {})
    maybeStartRecording()
  }

  const attachRemoteAudioTrack = (track) => {
    const audio = remoteAudioRef.current
    if (!audio || !track || track.kind !== 'audio') return
    const stream = new MediaStream([track])
    if (audio.srcObject !== stream) audio.srcObject = stream
    audio.muted = false
    audio.volume = 1
    audio.play().catch(() => {})
    latencyRef.current?.attachBotAudioTrack(track)
    maybeStartRecording()
  }

  const maybeStartRecording = () => {
    if (recorderRef.current) return
    if (typeof window === 'undefined' || typeof window.MediaRecorder === 'undefined') return
    const client = clientRef.current
    const localVideoStream = mediaStreamRef.current
    const localAudioTrack = client?.tracks?.()?.local?.audio
    const remoteAudioStream = remoteAudioRef.current?.srcObject
    if (!localVideoStream || !localAudioTrack) return
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      const dest = audioContext.createMediaStreamDestination()
      audioContext.createMediaStreamSource(new MediaStream([localAudioTrack])).connect(dest)
      if (remoteAudioStream && remoteAudioStream.getAudioTracks?.().length > 0) {
        audioContext.createMediaStreamSource(remoteAudioStream).connect(dest)
      }
      const combined = new MediaStream([
        ...localVideoStream.getVideoTracks(),
        ...dest.stream.getAudioTracks(),
      ])
      const mimeType = pickRecorderMimeType()
      const recorder = mimeType ? new MediaRecorder(combined, { mimeType }) : new MediaRecorder(combined)
      recorderChunksRef.current = []
      recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) recorderChunksRef.current.push(e.data) }
      recorder.start(1000)
      recorderRef.current = recorder
      recorderContextRef.current = audioContext
    } catch (err) {
      console.warn('Recording failed to start:', err)
    }
  }

  const stopRecording = () =>
    new Promise((resolve) => {
      const recorder = recorderRef.current
      const audioContext = recorderContextRef.current
      const cleanup = () => {
        recorderRef.current = null
        recorderContextRef.current = null
        if (audioContext && audioContext.state !== 'closed') audioContext.close().catch(() => {})
      }
      if (!recorder || recorder.state === 'inactive') { cleanup(); resolve(); return }
      recorder.onstop = () => { recorderChunksRef.current = []; cleanup(); resolve() }
      try { recorder.stop() } catch { cleanup(); resolve() }
    })

  const attachAvatarVideoTrack = (track) => {
    const video = avatarVideoRef.current
    const mediaTrack = track?.mediaStreamTrack
    if (!video || !mediaTrack || mediaTrack.kind !== 'video') return
    const stream = new MediaStream([mediaTrack])
    if (video.srcObject !== stream) video.srcObject = stream
    setHasAvatarVideo(true)
    video.play().catch(() => {})
    latencyRef.current?.attachAvatarVideo(video, track)
  }

  const startLiveAvatarSession = async () => {
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken() || '',
    }
    if (avatarId) headers['X-Avatar-Id'] = avatarId
    if (voiceGender) headers['X-Voice-Gender'] = voiceGender
    if (voiceMapB64) headers['X-Voice-Map-B64'] = voiceMapB64
    const sessionRequestedAt = performance.now()
    const response = await fetch('/api/realtime/live_avatar_sessions', {
      method: 'POST',
      headers,
      body: JSON.stringify({}),
    })
    const payload = await response.json()
    if (!response.ok) throw new Error(payload?.error || payload?.detail || 'Unable to start the avatar session.')
    latencyRef.current?.mark(`LiveAvatar session created (${Math.round(performance.now() - sessionRequestedAt)}ms)`)
    const room = new Room({ adaptiveStream: true, dynacast: true })
    room.on(RoomEvent.TrackSubscribed, (t) => {
      latencyRef.current?.mark(`LiveKit ${t?.kind || 'unknown'} track subscribed`)
      attachAvatarVideoTrack(t)
    })
    avatarRoomRef.current = room
    avatarSessionRef.current = payload
    const roomConnectStartedAt = performance.now()
    await room.connect(payload.livekit_url, payload.livekit_client_token)
    latencyRef.current?.mark(`LiveKit room connected (${Math.round(performance.now() - roomConnectStartedAt)}ms)`)
    return payload
  }

  const stopLiveAvatarSession = async () => {
    if (avatarRoomRef.current) { avatarRoomRef.current.disconnect(); avatarRoomRef.current = null }
    if (avatarVideoRef.current) avatarVideoRef.current.srcObject = null
    setHasAvatarVideo(false)
    const sessionId = avatarSessionRef.current?.session_id
    avatarSessionRef.current = null
    if (!sessionId) return
    try {
      await fetch(`/api/realtime/live_avatar_sessions/${sessionId}`, {
        method: 'DELETE',
        headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken() || '' },
      })
    } catch (e) { console.warn('Avatar cleanup failed:', e) }
  }

  const cleanupCall = async () => {
    latencyRef.current?.stop()
    latencyRef.current = null
    if (recorderRef.current) await stopRecording()
    if (clientRef.current) {
      try { await clientRef.current.disconnect() } catch (_) {}
      clientRef.current = null
      setPcClient(null)
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop())
      mediaStreamRef.current = null
    }
    if (localVideoRef.current) localVideoRef.current.srcObject = null
    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null
    await stopLiveAvatarSession()
    setHasVideo(false)
  }

  const startCall = async () => {
    setStatus('starting')
    setError('')
    setAvatarNotice('')
    setSecondsLeft(timeLimitSeconds)
    autoEndedRef.current = false
    callStartedRef.current = true

    latencyRef.current?.stop()
    latencyRef.current = createLatencyProbe()
    latencyRef.current.mark(`call starting (model=${model})`)

    let avatar = null
    try { avatar = await startLiveAvatarSession() }
    catch (e) {
      console.warn('Avatar unavailable:', e)
      setAvatarNotice('Avatar unavailable — continuing in audio-only mode.')
    }

    try {
      const client = new PipecatClient({
        transport: new SmallWebRTCTransport(),
        enableMic: true,
        enableCam: true,
        callbacks: {
          onConnected: () => {
            latencyRef.current?.mark('pipecat WebRTC connected')
            setStatus('connected')
          },
          onDisconnected: () => setStatus((c) => (c === 'connected' ? 'ended' : c)),
          onError: (m) => {
            setError(m?.data?.error || m?.message || 'Realtime connection error')
            setStatus('error')
          },
          onBotReady: () => latencyRef.current?.mark('pipecat bot ready'),
          onUserStartedSpeaking: () => latencyRef.current?.markUserStartedSpeaking(),
          onUserStoppedSpeaking: () => latencyRef.current?.markUserStoppedSpeaking(),
          onBotStartedSpeaking: () => latencyRef.current?.markBotStartedSpeaking(),
          onBotStoppedSpeaking: () => latencyRef.current?.markBotStoppedSpeaking(),
          onTrackStarted: (track, participant) => {
            if (track?.kind === 'audio' && !participant?.local) attachRemoteAudioTrack(track)
            else attachLocalVideoTrack(client)
          },
          onCamUpdated: () => attachLocalVideoTrack(client),
        },
      })
      clientRef.current = client
      setPcClient(client)

      await client.initDevices()
      attachLocalVideoTrack(client)
      const headers = {
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken() || '',
        'X-Triage-Model': model,
      }
      if (avatar) headers['X-Live-Avatar-Session-ID'] = avatar.session_id
      const promptB64 = systemPromptForLanguage(systemPromptB64, language)
      if (promptB64) headers['X-System-Prompt-B64'] = promptB64
      if (voiceGender) headers['X-Voice-Gender'] = voiceGender
      if (voiceMapB64) headers['X-Voice-Map-B64'] = voiceMapB64
      if (conversationUuid) headers['X-Conversation-Uuid'] = conversationUuid
      // No intake schema for patient sim — pipecat will fall back to the default
      // tools but the system prompt instructs the patient never to call them.
      headers['X-Intake-Schema-B64'] = btoa('[]')

      await client.connect({
        webrtcRequestParams: {
          endpoint: '/api/realtime/offer',
          headers: new Headers(headers),
        },
      })
      attachLocalVideoTrack(client)
      latencyRef.current?.setPipecatClient(client)
      setStatus('connected')
    } catch (e) {
      setError(e.message || 'Unable to start the session.')
      await cleanupCall()
      setStatus('error')
    }
  }

  const endCallAndPromptDiagnosis = async () => {
    if (autoEndedRef.current) return
    autoEndedRef.current = true
    setStatus('ended')
    await cleanupCall()
    setShowDiagnosisForm(true)
  }

  const handleStop = async () => {
    await endCallAndPromptDiagnosis()
  }

  const submitDiagnosis = async () => {
    if (grading) return
    setGrading(true)
    setError('')
    try {
      // Make sure the call has been torn down so messages have been flushed
      if (clientRef.current || recorderRef.current) await cleanupCall()
      const response = await fetch(`/training/${agentSlug}/${conversationUuid}/grade`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken() || '',
        },
        body: JSON.stringify({ trainee_diagnosis: diagnosis }),
      })
      const body = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(body?.error || 'Grading failed.')
      setGrade(body.grade)
    } catch (e) {
      setError(e.message || 'Grading failed.')
    } finally {
      setGrading(false)
    }
  }

  // Countdown timer
  useEffect(() => {
    if (status !== 'connected') return
    timerRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current)
          endCallAndPromptDiagnosis()
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(timerRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearInterval(timerRef.current)
      cleanupCall()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const callActive = status === 'connected' || status === 'starting'
  const showStartOverlay = status === 'idle' || status === 'error'
  const timerCritical = secondsLeft <= 30
  const timerWarn = !timerCritical && secondsLeft <= 60

  return (
    <div className="min-h-screen bg-background">
      <audio ref={remoteAudioRef} autoPlay playsInline />
      <header className="border-b bg-background/95">
        <div className="container mx-auto flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <Link href="/training" className="text-sm font-medium text-slate-600 hover:text-slate-900">
              ← All cases
            </Link>
            <span className="text-xl font-bold tracking-tight">{caseName}</span>
            {caseDescription && (
              <span className="hidden text-sm text-slate-500 sm:inline">· {caseDescription}</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {status === 'connected' && (
              <div
                className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 font-mono text-2xl font-bold tabular-nums shadow-sm ${
                  timerCritical
                    ? 'border-rose-300 bg-rose-50 text-rose-700'
                    : timerWarn
                      ? 'border-amber-300 bg-amber-50 text-amber-700'
                      : 'border-slate-200 bg-slate-50 text-slate-700'
                }`}
                title="Time remaining"
              >
                <Clock className="h-5 w-5" />
                {formatClock(secondsLeft)}
              </div>
            )}
            {callActive && (
              <Button onClick={handleStop} variant="outline" size="sm">
                <PhoneOff />
                End call
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto grid gap-8 px-4 py-10 lg:grid-cols-[minmax(0,1fr)_400px]">
        <section className="relative aspect-video overflow-hidden rounded-lg border bg-card shadow-sm">
          <img
            src={avatarPreviewUrl || doctorImage}
            onError={(e) => {
              if (e.currentTarget.src.indexOf('/doctor.svg') === -1) e.currentTarget.src = '/doctor.svg'
            }}
            alt="Patient avatar"
            className={`absolute inset-0 h-full w-full object-cover transition-opacity ${
              hasAvatarVideo ? 'opacity-0' : 'opacity-100'
            }`}
          />
          <video
            ref={avatarVideoRef}
            autoPlay
            playsInline
            muted
            className={`absolute inset-0 h-full w-full object-cover transition-opacity ${
              hasAvatarVideo ? 'opacity-100' : 'opacity-0'
            }`}
          />
          <AudioOrb
            audioRef={remoteAudioRef}
            active={status === 'connected' && !hasAvatarVideo && Boolean(avatarNotice)}
          />
          {showStartOverlay && !grade && (
            <div className="absolute inset-0 flex flex-col items-end justify-end gap-3 bg-gradient-to-t from-black/75 via-black/15 to-transparent p-6 text-center sm:items-center sm:justify-end sm:pb-8">
              <label className="flex items-center gap-2 text-sm text-white/90">
                <span className="font-medium">Language</span>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="rounded-md border border-white/30 bg-white/10 px-2 py-1 text-sm text-white shadow-sm backdrop-blur focus:outline-none focus:ring-2 focus:ring-white/60"
                >
                  {LANGUAGES.map((l) => (
                    <option key={l} value={l} className="text-slate-900">
                      {l}
                    </option>
                  ))}
                </select>
              </label>
              <Button onClick={startCall} size="lg" className="h-16 gap-3 px-10 text-lg font-semibold shadow-2xl">
                <Phone className="size-5" />
                Start the case
              </Button>
              <p className="text-sm text-white/90">
                You'll have {Math.round(timeLimitSeconds / 60)} minutes to reach a diagnosis.
              </p>
            </div>
          )}
          {status === 'starting' && (
            <div className="absolute inset-0 flex flex-col items-end justify-end gap-3 bg-gradient-to-t from-black/75 via-black/15 to-transparent p-6 text-white/95 sm:items-center sm:justify-end sm:pb-8">
              <Loader2 className="size-10 animate-spin" />
              <p className="text-sm font-medium tracking-wide">Connecting…</p>
            </div>
          )}
          {status === 'ended' && !grade && (
            <div className="absolute inset-0 flex flex-col items-end justify-end gap-2 bg-gradient-to-t from-black/75 via-black/15 to-transparent p-6 text-white sm:items-center sm:justify-end sm:pb-8">
              <p className="text-xl font-semibold tracking-tight">Time's up — what's your diagnosis?</p>
              <p className="text-sm text-white/85">Enter your best guess on the right.</p>
            </div>
          )}
          {callActive && (
            <div className="absolute bottom-2 right-2 h-24 w-32 overflow-hidden rounded-md border-2 border-white/90 bg-zinc-950 shadow-2xl sm:bottom-4 sm:right-4 sm:h-36 sm:w-52">
              <video
                ref={(node) => {
                  localVideoRef.current = node
                  if (node && mediaStreamRef.current) attachLocalVideoTrack()
                }}
                autoPlay
                playsInline
                muted
                className="h-full w-full object-cover"
              />
              {!hasVideo && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/70 text-xs text-white/80">
                  Camera off
                </div>
              )}
            </div>
          )}
        </section>

        <aside className="space-y-4">
          {!showDiagnosisForm && (
            <section className="rounded-lg border bg-card p-5">
              <h2 className="text-base font-semibold tracking-tight">Patient encounter</h2>
              <p className="mt-2 text-sm text-slate-600">
                Conduct a focused history. Build rapport. Reach a diagnosis before the timer hits zero.
              </p>
              <div className="mt-4 flex flex-col gap-2">
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    onClick={() => {
                      setMuted((m) => {
                        const next = !m
                        clientRef.current?.enableMic(!next)
                        return next
                      })
                    }}
                    disabled={status !== 'connected'}
                    variant={muted ? 'outline' : 'default'}
                  >
                    {muted ? <MicOff /> : <Mic />}
                    {muted ? 'Muted' : 'Live'}
                  </Button>
                  <Button
                    onClick={() => {
                      setCamOff((c) => {
                        const next = !c
                        try { clientRef.current?.enableCam(!next) } catch (e) { console.warn('enableCam failed:', e) }
                        if (next) setHasVideo(false)
                        return next
                      })
                    }}
                    disabled={status !== 'connected'}
                    variant={camOff ? 'outline' : 'default'}
                  >
                    {camOff ? <VideoOff /> : <Video />}
                    {camOff ? 'Camera off' : 'Camera on'}
                  </Button>
                </div>
                {status === 'connected' && (
                  <Button onClick={endCallAndPromptDiagnosis} variant="outline" className="w-full">
                    I have my diagnosis
                  </Button>
                )}
                <DevicePickers client={pcClient} status={status} />
              </div>
            </section>
          )}

          {showDiagnosisForm && !grade && (
            <section className="rounded-lg border bg-card p-5">
              <h2 className="text-base font-semibold tracking-tight">Your diagnosis</h2>
              <p className="mt-1 text-sm text-slate-600">
                Be specific. The grader will compare against the hidden ground truth.
              </p>
              <textarea
                className="mt-3 min-h-[110px] w-full rounded-md border bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-300"
                placeholder="e.g. Acute appendicitis"
                value={diagnosis}
                onChange={(e) => setDiagnosis(e.target.value)}
                disabled={grading}
              />
              <Button
                onClick={submitDiagnosis}
                disabled={grading}
                className="mt-3 w-full"
              >
                {grading ? <Loader2 className="size-4 animate-spin" /> : null}
                {grading ? 'Grading…' : 'Submit for grading'}
              </Button>
            </section>
          )}

          {grade && <GradePanel grade={grade} diagnosis={diagnosis} agentSlug={agentSlug} />}

          {avatarNotice && (
            <section className="rounded-lg border border-amber-500/20 bg-amber-50 p-4 text-sm text-amber-900">
              {avatarNotice}
            </section>
          )}

          {error && (
            <section className="rounded-lg border border-destructive/20 bg-destructive/10 p-5">
              <p className="text-sm text-destructive">{error}</p>
            </section>
          )}
        </aside>
      </main>
    </div>
  )
}

function GradePanel({ grade, diagnosis, agentSlug }) {
  const verdict = VERDICT_META[grade.verdict] || VERDICT_META.partial
  const VerdictIcon = verdict.icon
  const overall = typeof grade.overall === 'number' ? grade.overall : null

  return (
    <section className="space-y-4">
      <div className={`flex items-center justify-between rounded-lg border px-4 py-3 ${verdict.tone}`}>
        <div className="flex items-center gap-2">
          <VerdictIcon className="h-5 w-5" />
          <span className="text-sm font-semibold">{verdict.label}</span>
        </div>
        {overall !== null && (
          <span className="font-mono text-xl font-bold tabular-nums">{overall}/100</span>
        )}
      </div>

      <div className="rounded-lg border bg-card p-5">
        <h3 className="text-sm font-semibold tracking-tight">Scores</h3>
        <div className="mt-3 space-y-3">
          <ScoreRow label="Communication" value={grade.communication} />
          <ScoreRow label="Bedside manner" value={grade.bedside_manner} />
          <ScoreRow label="History taking" value={grade.history_taking} />
          <ScoreRow label="Diagnostic accuracy" value={grade.diagnostic_accuracy} />
        </div>
      </div>

      {grade.summary && (
        <div className="rounded-lg border bg-card p-5">
          <h3 className="text-sm font-semibold tracking-tight">Summary</h3>
          <p className="mt-2 text-sm text-slate-700">{grade.summary}</p>
        </div>
      )}

      {Array.isArray(grade.strengths) && grade.strengths.length > 0 && (
        <div className="rounded-lg border bg-card p-5">
          <h3 className="text-sm font-semibold tracking-tight text-emerald-700">What worked</h3>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-700">
            {grade.strengths.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      )}

      {Array.isArray(grade.improvements) && grade.improvements.length > 0 && (
        <div className="rounded-lg border bg-card p-5">
          <h3 className="text-sm font-semibold tracking-tight text-rose-700">Work on</h3>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-700">
            {grade.improvements.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      )}

      {diagnosis && (
        <div className="rounded-lg border bg-card p-5">
          <h3 className="text-sm font-semibold tracking-tight">Your diagnosis</h3>
          <p className="mt-2 text-sm text-slate-700">{diagnosis}</p>
        </div>
      )}

      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" onClick={() => router.visit('/training')}>
          Another case
        </Button>
        <Button className="flex-1" onClick={() => router.visit(`/training/${agentSlug}`)}>
          Retry this case
        </Button>
      </div>
    </section>
  )
}

function ScoreRow({ label, value }) {
  const score = typeof value?.score === 'number' ? value.score : null
  const pct = score === null ? 0 : Math.max(0, Math.min(100, (score / 10) * 100))
  const tone = score === null
    ? 'bg-slate-300'
    : score >= 8 ? 'bg-emerald-500'
    : score >= 6 ? 'bg-sky-500'
    : score >= 4 ? 'bg-amber-500'
    : 'bg-rose-500'

  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-slate-700">{label}</span>
        <span className="font-mono tabular-nums text-slate-600">
          {score === null ? '—' : `${score}/10`}
        </span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
      </div>
      {value?.comment && <p className="mt-1 text-xs text-slate-500">{value.comment}</p>}
    </div>
  )
}
